import type { Tab } from '../../../types/tabs';

export interface PersistedTab {
  id: string;
  label: string;
  path: string;
  isClosable: boolean;
  isPinned: boolean;
  lastAccessed: Date | string;
}

export interface PersistedTabState {
  tabs: PersistedTab[];
  activeTabId: string | null;
}

const isPersistedTab = (value: unknown): value is PersistedTab => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<PersistedTab>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.label === 'string' &&
    typeof candidate.path === 'string' &&
    typeof candidate.isClosable === 'boolean' &&
    typeof candidate.isPinned === 'boolean' &&
    (candidate.lastAccessed instanceof Date || typeof candidate.lastAccessed === 'string')
  );
};

export const toPersistedTab = (tab: Tab): PersistedTab => ({
  id: tab.id,
  label: tab.label,
  path: tab.path,
  isClosable: tab.isClosable,
  isPinned: tab.isPinned,
  lastAccessed: tab.lastAccessed,
});

export const fromPersistedTab = (persistedTab: PersistedTab): Tab => ({
  id: persistedTab.path,
  label: persistedTab.label,
  path: persistedTab.path,
  isClosable: persistedTab.isClosable,
  isPinned: persistedTab.isPinned,
  lastAccessed:
    persistedTab.lastAccessed instanceof Date
      ? persistedTab.lastAccessed
      : new Date(persistedTab.lastAccessed),
  component: () => null,
});

export const parsePersistedTabState = (raw: string | null): PersistedTabState | null => {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedTabState> | PersistedTab[];

    if (Array.isArray(parsed)) {
      const tabs = parsed.filter(isPersistedTab);
      return {
        tabs,
        activeTabId: tabs[0]?.id ?? null,
      };
    }

    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const tabs = Array.isArray(parsed.tabs) ? parsed.tabs.filter(isPersistedTab) : [];
    const activeTabId = typeof parsed.activeTabId === 'string' ? parsed.activeTabId : null;

    return {
      tabs,
      activeTabId,
    };
  } catch {
    return null;
  }
};
